import { otpLoginService, otpRequestService, playerStatusService } from "../services/login-services";
import {
    phoneSendVerificationService,
    verifyPhoneService,
    newPasswordService,
} from "../services/registration-services";
import { callRegisterUser } from "@/api-connector/play-digital-api/metods/account/registration-api";
import { isErrorDto, parseErrorMessage } from "../utils/user-account-helper";
import useAuthenticationFlows, { authenticationStep } from "./use-authentication-flows";
import useRegistrationConsents from "./use-registration-consents";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { authenticationData, authenticationErrors } from "./use-authentication-data";

const { nextFlowStep, prevFlowStep, openRegistrationModal, openRegPasswordModal, openVerifyPhoneModal } =
    useAuthenticationFlows();

export const sendPinProcess = async (sendAllways = false): Promise<boolean> => {
    try {
        if (authenticationStep.value == "RegVerPhone") {
            await phoneSendVerificationService(authenticationData.userName);
        } else {
            await otpRequestService(authenticationData.userName, sendAllways);
        }
    } catch (ex: unknown) {
        authenticationErrors.pinErrorMessage = ex as string;
        return false;
    }
    return true;
};

let otpToken: string = null;
export const pinVerificationProcess = async (): Promise<boolean> => {
    try {
        if (authenticationStep.value == "RegVerPhone") {
            await verifyPhoneService(authenticationData.userName, authenticationData.pin);
        } else {
            otpToken = await otpLoginService(authenticationData.userName, authenticationData.pin);
        }
    } catch (ex: unknown) {
        authenticationErrors.pinErrorMessage = ex as string;
        return false;
    }

    return true;
};

export const newPasswordProcess = async (): Promise<boolean> => {
    try {
        await newPasswordService(authenticationData.password, otpToken);
        otpToken = null;
        authenticationData.password = "";
    } catch (ex: unknown) {
        authenticationErrors.newPasswordErrorMessage = ex as string;
        return false;
    }
    return true;
};

export const playerStatusProcess = async (): Promise<boolean> => {
    try {
        const playerStatus = await playerStatusService(authenticationData.userName);

        if (!playerStatus.isRegistered) {
            openRegistrationModal();
            return false;
        } else if (!playerStatus.isPasswordSet) {
            if (openRegPasswordModal()) {
                await sendPinProcess();
            }
            return false;
        } else if (!playerStatus.isPhoneVerified) {
            if (openVerifyPhoneModal()) {
                await sendPinProcess();
            }
            return false;
        }
    } catch (ex: unknown) {
        authenticationErrors.phoneErrorMessage = ex as string;
        return false;
    }

    return true;
};

const { getConsetsForRegApi } = useRegistrationConsents();
export const registrationProcess = async (): Promise<boolean> => {
    const consents = await getConsetsForRegApi(authenticationData.consents);

    try {
        await callRegisterUser(authenticationData.userName, authenticationData.password, consents);
    } catch (ex: unknown) {
        const errorMessage = parseErrorMessage(ex, "Chyba při registraci uživatele.");

        if (isErrorDto(ex) && ex.code.includes("PHONE")) {
            authenticationErrors.phoneErrorMessage = errorMessage;
            return false;
        }

        authenticationErrors.passwordErrorMessage = errorMessage;
        return false;
    }

    authenticationData.password = "";
    return true;
};

// State machine
const nextAuthenticationStep = async () => {
    let canNextStep = true;
    Loader.show();

    if (authenticationStep.value == "LoginPhone") {
        canNextStep = await playerStatusProcess();
    } else if (authenticationStep.value == "ForgPinGen") {
        canNextStep = await sendPinProcess();
    } else if (
        authenticationStep.value == "ForgPinLog" ||
        authenticationStep.value == "RegPinLog" ||
        authenticationStep.value == "RegVerPhone"
    ) {
        canNextStep = await pinVerificationProcess();
    } else if (authenticationStep.value == "ForgNewPass" || authenticationStep.value == "RegNewPass") {
        canNextStep = await newPasswordProcess();
    } else if (authenticationStep.value == "RegPersonal") {
        canNextStep = await registrationProcess();
    }

    if (canNextStep) {
        nextFlowStep();
    }

    Loader.hide();
};

const prevAuthenticationStep = () => {
    prevFlowStep();
};

export default function useAuthentication() {
    return {
        sendPinProcess,
        nextAuthenticationStep,
        prevAuthenticationStep,
    };
}
